// src/views/components/chatBot/chatBot.js
import React, { useState, useEffect, useRef } from 'react';
import ForumIcon from '@mui/icons-material/Forum';
import styles from './chatBot.module.css';
import chatBotData from './chatBotData.json';

import ServiciosChatBot from 'views/ChatBot/components/serviciosChatBot';
import { Tooltip } from '@mui/material';

// ── Constantes de estado del flujo ──────────────────────────────────────────
const FLOW = {
    WELCOME: 'WELCOME', // mensaje de bienvenida + ¿puedo ayudarte?
    QUESTIONS_LIST: 'QUESTIONS_LIST', // lista de preguntas
    SHOWING_ANSWER: 'SHOWING_ANSWER' // mostrando respuesta + ¿más ayuda?
};

// ── Íconos SVG inline ────────────────────────────────────────────────────────
const ChatIcon = () => (
    <svg className={styles.triggerIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="white" />
    </svg>
);

const CloseIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M1 1l12 12M13 1L1 13" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// ── Componente de indicador "escribiendo" ────────────────────────────────────
const TypingIndicator = () => (
    <div className={styles.typing} role="status" aria-label="Escribiendo...">
        <div className={styles.typingDot} />
        <div className={styles.typingDot} />
        <div className={styles.typingDot} />
    </div>
);

// ── Componente principal ─────────────────────────────────────────────────────
const ChatBot = ({ style, triggerClassName = '' }) => {
    const serviciosChatBot = ServiciosChatBot();

    const [isOpen, setIsOpen] = useState(false);
    const [flow, setFlow] = useState(FLOW.WELCOME);
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const bodyRef = useRef(null);

    const [chatBotDataInfo, setChatBotDataInfo] = useState([]);

    useEffect(() => {
        serviciosChatBot.listarChatBot((response) => {
            // Manejar los datos recibidos del servidor
            if (response?.status == 1) {
                setChatBotDataInfo(response.data);
            }
        });
    }, []);

    let { welcome, helpPrompt, continuePrompt, faqs } = chatBotDataInfo;

    if (Array.isArray(faqs)) {
        faqs = [...faqs].sort((a, b) => Number(a.orden) - Number(b.orden));
    } else {
        faqs = [];
    }

    // Auto-scroll al último mensaje
    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    // Inicializar el chat cuando se abre por primera vez
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            showWelcome();
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Helpers para agregar mensajes ──────────────────────────────────────────
    const addMessage = (msg) => {
        setMessages((prev) => [...prev, { id: Date.now() + Math.random(), ...msg }]);
    };

    const simulateTypingThen = (delay, callback) => {
        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            callback();
        }, delay);
    };

    // ── Flujo de conversación ──────────────────────────────────────────────────

    const showWelcome = () => {
        setFlow(FLOW.WELCOME);
        simulateTypingThen(700, () => {
            addMessage({ type: 'bot', text: welcome });
            simulateTypingThen(600, () => {
                if (faqs.length === 0) {
                    addMessage({ type: 'bot', text: 'Disculpa, de momento no puedo ayudarte. Intenta más tarde.' });
                } else {
                    addMessage({ type: 'yes_no_initial' });
                }
            });
        });
    };

    const handleInitialYes = () => {
        // Eliminar el bloque de botones Sí/No inicial
        setMessages((prev) => prev.filter((m) => m.type !== 'yes_no_initial'));
        addMessage({ type: 'user', text: 'Sí, necesito ayuda.' });
        simulateTypingThen(700, () => {
            if (faqs.length === 0) {
                addMessage({ type: 'bot', text: 'Disculpa, de momento no puedo ayudarte. Intenta más tarde.' });
            } else {
                addMessage({ type: 'bot', text: helpPrompt });
                simulateTypingThen(400, () => {
                    addMessage({ type: 'questions_list' });
                    setFlow(FLOW.QUESTIONS_LIST);
                });
            }
        });
    };

    const handleInitialNo = () => {
        setMessages((prev) => prev.filter((m) => m.type !== 'yes_no_initial'));
        addMessage({ type: 'user', text: 'No, gracias.' });
        simulateTypingThen(600, () => {
            addMessage({ type: 'bot', text: '¡Entendido! Estoy aquí cuando me necesites.' });
        });
    };

    const handleSelectQuestion = (faq) => {
        // Ocultar la lista de preguntas
        setMessages((prev) => prev.filter((m) => m.type !== 'questions_list'));
        addMessage({ type: 'user', text: faq.question });
        simulateTypingThen(900, () => {
            addMessage({ type: 'bot', text: faq.answer });
            simulateTypingThen(800, () => {
                addMessage({ type: 'bot', text: continuePrompt });
                simulateTypingThen(600, () => {
                    addMessage({ type: 'yes_no_continue' });
                    setFlow(FLOW.SHOWING_ANSWER);
                });
            });
        });
    };

    const handleContinueYes = () => {
        setMessages((prev) => prev.filter((m) => m.type !== 'yes_no_continue'));
        addMessage({ type: 'user', text: 'Sí, tengo otra pregunta.' });
        simulateTypingThen(600, () => {
            if (faqs.length === 0) {
                addMessage({ type: 'bot', text: 'Disculpa, de momento no puedo ayudarte. Intenta más tarde.' });
            } else {
                addMessage({ type: 'bot', text: helpPrompt });
                simulateTypingThen(400, () => {
                    addMessage({ type: 'questions_list' });
                    setFlow(FLOW.QUESTIONS_LIST);
                });
            }
        });
    };

    const handleContinueNo = () => {
        setMessages((prev) => prev.filter((m) => m.type !== 'yes_no_continue'));
        addMessage({ type: 'user', text: 'No, eso es todo. ¡Gracias!' });
        simulateTypingThen(600, () => {
            addMessage({
                type: 'bot',
                text: '¡Con gusto! Si más adelante tienes alguna pregunta, aquí estaré.'
            });
            setFlow(FLOW.WELCOME);
        });
    };

    // ── Cerrar / abrir ──────────────────────────────────────────────────────────
    const handleOpen = () => setIsOpen(true);
    const handleClose = () => {
        setIsOpen(false);
        // Resetear el chat para próxima apertura
        setMessages([]);
        setIsTyping(false);
        setFlow(FLOW.WELCOME);
    };

    // ── Render de cada tipo de mensaje ─────────────────────────────────────────
    const renderMessage = (msg) => {
        switch (msg.type) {
            case 'bot':
                return (
                    <div key={msg.id} className={styles.botBubble}>
                        {msg.text}
                    </div>
                );

            case 'user':
                return (
                    <div key={msg.id} className={styles.userBubble}>
                        {msg.text}
                    </div>
                );

            case 'yes_no_initial':
                return (
                    <div key={msg.id} className={styles.yesNoRow}>
                        <button className={styles.yesBtn} onClick={handleInitialYes}>
                            Sí
                        </button>
                        <button className={styles.noBtn} onClick={handleInitialNo}>
                            No
                        </button>
                    </div>
                );

            case 'yes_no_continue':
                return (
                    <div key={msg.id} className={styles.yesNoRow}>
                        <button className={styles.yesBtn} onClick={handleContinueYes}>
                            Sí
                        </button>
                        <button className={styles.noBtn} onClick={handleContinueNo}>
                            No
                        </button>
                    </div>
                );

            case 'questions_list':
                if (!faqs || faqs.length === 0) {
                    return null;
                }
                return (
                    <div key={msg.id} className={styles.optionsList}>
                        {faqs.map((faq) => (
                            <button key={faq.id} className={styles.optionBtn} onClick={() => handleSelectQuestion(faq)}>
                                {faq.question}
                            </button>
                        ))}
                    </div>
                );

            default:
                return null;
        }
    };

    // ── JSX principal ───────────────────────────────────────────────────────────
    return (
        <>
            {/* Botón flotante colapsado */}
            {!isOpen && (
                <Tooltip title="Abrir chat de ayuda" placement="top">
                    <button className={triggerClassName} onClick={handleOpen} aria-label="Abrir chat de ayuda" style={style}>
                        <ChatIcon />
                        Chat
                    </button>
                </Tooltip>
            )}

            {/* Ventana del chat */}
            {isOpen && (
                <div className={styles.chatOverlay} role="dialog" aria-modal="true" aria-label="Asistente virtual">
                    {/* Encabezado */}
                    <div className={styles.chatHeader}>
                        <div className={styles.headerLeft}>
                            <div className={styles.avatarWrap} aria-hidden="true">
                                <ForumIcon style={{ color: '#ffff' }} />
                            </div>
                            <div className={styles.headerInfo}>
                                <span className={styles.headerName}>Asistente Virtual</span>
                                <span className={styles.headerStatus}>
                                    <span className={styles.statusDot} />
                                    En línea
                                </span>
                            </div>
                        </div>
                        <button className={styles.closeBtn} onClick={handleClose} aria-label="Cerrar chat">
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Cuerpo de mensajes */}
                    <div className={styles.chatBody} ref={bodyRef}>
                        {messages.map(renderMessage)}
                        {isTyping && <TypingIndicator />}
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatBot;
